import { startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUpdatePurchaseOrderDates } from '@/services/apis/mutations/purchase-orders/use-update-purchase-order-dates';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';
import {
  type DateFieldKey,
  getPurchaseOrderDates,
  PURCHASE_ORDER_DATE_ORDER,
  type PurchaseOrderDates,
  purchaseOrderDatesSchema,
} from '../schema/purchase-order-dates-schema';

function getDisabledDates(dates: PurchaseOrderDates, field: DateFieldKey) {
  const index = PURCHASE_ORDER_DATE_ORDER.indexOf(field);
  const timestamps = PURCHASE_ORDER_DATE_ORDER.map((key) =>
    dates[key] ? startOfDay(dates[key]).getTime() : undefined,
  );
  const earlier = timestamps
    .slice(0, index)
    .filter((value) => value !== undefined);
  const later = timestamps
    .slice(index + 1)
    .filter((value) => value !== undefined);
  if (earlier.length === 0 && later.length === 0) {
    return;
  }
  const minimum = Math.max(...earlier);
  const maximum = Math.min(...later);
  return (date: Date) => {
    const timestamp = startOfDay(date).getTime();
    return timestamp < minimum || timestamp > maximum;
  };
}

export function usePurchaseOrderMainDate(purchaseOrderId: string) {
  const { t } = useTranslation('common');
  const { data: purchaseOrder } = useGetPurchaseOrder(purchaseOrderId);
  const { mutate: updateDates } = useUpdatePurchaseOrderDates(purchaseOrderId);

  const apiDates = useMemo(
    () =>
      getPurchaseOrderDates({
        productionEndDate: purchaseOrder.productionEndDate,
        expeditionDate: purchaseOrder.expeditionDate,
        estimatedArrivalDate: purchaseOrder.estimatedArrivalDate,
        deliveryDate: purchaseOrder.deliveryDate,
      }),
    [
      purchaseOrder.productionEndDate,
      purchaseOrder.expeditionDate,
      purchaseOrder.estimatedArrivalDate,
      purchaseOrder.deliveryDate,
    ],
  );

  const [dates, setDates] =
    useState<Record<DateFieldKey, Date | undefined>>(apiDates);

  useEffect(() => {
    setDates(apiDates);
  }, [apiDates]);

  const handleDateChange = useCallback(
    (key: DateFieldKey) => (date: Date | undefined) => {
      const next = { ...dates, [key]: date };
      setDates(next);

      const validation = purchaseOrderDatesSchema.safeParse(next);
      if (!validation.success) {
        return;
      }

      updateDates(
        {
          id: purchaseOrderId,
          productionEndDate: next.eop,
          expeditionDate: next.etd,
          estimatedArrivalDate: next.eta,
          deliveryDate: next.lis,
        },
        {
          onError: () => {
            setDates(apiDates);
            toast.error(t('error'));
          },
        },
      );
    },
    [dates, apiDates, purchaseOrderId, updateDates, t],
  );

  const validation = useMemo(() => {
    const data: PurchaseOrderDates = {
      eop: dates.eop,
      etd: dates.etd,
      eta: dates.eta,
      lis: dates.lis,
    };
    return purchaseOrderDatesSchema.safeParse(data);
  }, [dates.eop, dates.etd, dates.eta, dates.lis]);

  const firstIssue = validation.success
    ? undefined
    : validation.error.issues[0];
  return {
    dates,
    disabledDates: {
      eop: getDisabledDates(dates, 'eop'),
      etd: getDisabledDates(dates, 'etd'),
      eta: getDisabledDates(dates, 'eta'),
      lis: getDisabledDates(dates, 'lis'),
    },
    handleDateChange,
    validationErrorKey: firstIssue?.message ?? null,
    validationErrorParams:
      firstIssue?.code === 'custom' ? firstIssue.params : undefined,
  };
}
